/**
 * DeckStart — Deck studio's front door (2026-09-30): one box. Say what deck you need; a new deck opens and Lykke
 * builds it from the brand's data while you watch. No gallery, no steps. The brand is the one you are working on
 * (a picker only when the team has several).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { useActiveBrand } from '@/cowork/useActiveBrand';

const IDEAS = [
  'A plan for our Christmas campaign, based on what worked before',
  'Results of our latest campaign, for the client',
  'What we learned this year, and what to do next year',
  'Our top creators and why they work',
];

const DRAFT_KEY = 'deck-start-draft';

export function DeckStart({ onStart }: { onStart: (id: string, ask: string) => void }) {
  const { brandId, brands, activeBrand, selectBrand } = useActiveBrand();
  // The draft survives the page settling (the app shell can remount the page while the workspace loads) and a
  // trip away and back: what someone typed is never lost.
  const [text, setTextState] = useState(() => { try { return sessionStorage.getItem(DRAFT_KEY) || ''; } catch { return ''; } });
  const setText = (v: string) => { setTextState(v); try { if (v) sessionStorage.setItem(DRAFT_KEY, v); else sessionStorage.removeItem(DRAFT_KEY); } catch { /* storage off */ } };
  const [busy, setBusy] = useState(false);
  // Ideas from this brand's own campaigns ("Results of Merrell - UTMB, for the client"); generic ones until they load.
  const { data } = useQuery({ queryKey: ['cowork', 'deck-ideas', brandId || ''], queryFn: () => coworkApi.presentations.ideas(brandId), staleTime: 10 * 60_000 });
  const ideas = data?.ideas?.length ? data.ideas : IDEAS;

  const start = async (message: string) => {
    const ask = message.trim(); if (!ask || busy) return;
    setBusy(true);
    try {
      const created = await coworkApi.presentations.create({ title: 'Untitled deck', brandId, brandChosen: true });
      setText('');
      onStart(created.id, ask);
    } catch (e) { toast.error((e as Error).message); setBusy(false); }
  };

  return (
    <div className="mb-8 rounded-2xl border border-[#eaecf0] bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[17px] font-semibold text-[#101828]">What deck do you need?</div>
        {brands.length > 1 ? (
          <select value={brandId || ''} onChange={(e) => selectBrand(e.target.value)} className="rounded-lg border border-[#d0d5dd] bg-white px-2 py-1 text-[12.5px] font-medium text-[#344054]" aria-label="Brand">
            {brands.map((b) => <option key={b.brandId} value={b.brandId}>{b.brandName}</option>)}
          </select>
        ) : activeBrand ? <span className="rounded-full bg-[#f2f4f7] px-2.5 py-0.5 text-[12px] font-medium text-[#344054]">{activeBrand.brandName}</span> : null}
      </div>
      <div className="mt-3 flex items-end gap-2 rounded-xl border border-[#d0d5dd] px-3 py-2.5 focus-within:border-[#be43ff] focus-within:ring-2 focus-within:ring-[#be43ff]/15">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void start(text); } }}
          rows={Math.min(5, Math.max(2, text.split('\n').length + Math.floor(text.length / 90)))}
          placeholder="Say it like you would to a colleague — Lykke uses the brand's campaigns, results and Brand OS and builds the whole deck."
          disabled={busy}
          autoFocus
          className="min-w-0 flex-1 resize-none bg-transparent text-[14px] leading-snug text-[#101828] placeholder:text-[#98a2b3] focus:outline-none"
          aria-label="What deck do you need?"
        />
        <button onClick={() => void start(text)} disabled={!text.trim() || busy} className="rounded-lg bg-[#be43ff] p-2 text-white disabled:opacity-40" aria-label="Build the deck">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}</button>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {/* One tap builds it: an idea is already a complete ask. */}
        {ideas.map((idea) => <button key={idea} onClick={() => void start(idea)} disabled={busy} className="rounded-full border border-[#eaecf0] px-3 py-1 text-[12px] text-[#475467] hover:border-[#be43ff] hover:bg-[#fcf7ff]">{idea}</button>)}
      </div>
    </div>
  );
}
